import {
  ComponentConfig,
  type Config,
  AutoField,
  FieldLabel,
} from '@measured/puck';
import {
  ColumnsProps,
  FlexProps,
  ContainerProps,
  VerticalSpaceProps,
  SimplePageHeaderProps,
  BillingFormProps,
  TextFieldProps,
  ContactInfoProps,
  fontFamilies,
  TimerProps,
  ShippingMethodSelectProps,
  PaymentProps,
  SingleStepButtonSectionProps,
  TermsAndSubProps,
  AccordionProps,
  UpSellProps,
  CustomerReviewsProps,
  OrderDetailsProps,
  DividerProps,
  MarginProps,
  TextEditorProps,
  ButtonSectionProps,
  ImageUploaderProps,
  CustomerGuarnteesProps,
  StandardLayoutProps,
  SplitLayoutProps,
  PromoCodeProps,
  SavingCalculatorProps,
  PaymentRadioProps,
  PuckRenderPage,
  createComponentConfigs,
  ThemeColorComponent,
  ErrorColor,
  CustomCss,
  CustomScripts,
  ReturnButtonProps,
} from 'shared-components';
import { baseService } from 'services';
import { PixelTracking } from './config-components/pixel-tracking/PixelTracking';
import { useAppSelector, useAppDispatch } from 'hooks/reduxHooks';
import { StepperProps } from 'shared-components/src/page-builder/puck/reusable-props/Stepper';
type Props = {
  // Layouts
  SplitLayout: SplitLayoutProps;
  Stepper: StepperProps;
  StandardLayout: StandardLayoutProps;
  // Blocks
  Columns: ColumnsProps;
  Flex: FlexProps;
  VerticalSpace: VerticalSpaceProps;
  Divider: DividerProps;
  Container: ContainerProps;
  // Text
  TextField: TextFieldProps;
  TextEditor: TextEditorProps;
  // Buttons
  Button: ButtonSectionProps;
  ConfirmPurchase: SingleStepButtonSectionProps;
  ReturnButton: ReturnButtonProps;
  // Page Headers
  SimplePageHeader: SimplePageHeaderProps;
  // Form Components
  ContactInfo: ContactInfoProps;
  ShippingForm: MarginProps;
  BillingForm: BillingFormProps;
  ShippingMethodSelect: ShippingMethodSelectProps;
  Payment: PaymentProps;
  // Checkout Components
  Cart: MarginProps;
  Timer: TimerProps;
  TermsAndSub: TermsAndSubProps;
  Accordion: AccordionProps;
  UpSell: UpSellProps;
  CustomerReviews: CustomerReviewsProps;
  OrderDetails: OrderDetailsProps;
  CustomerGuarantees: CustomerGuarnteesProps;
  ImageUploader: ImageUploaderProps;
  PromoCode: PromoCodeProps;
  SavingCalculator: SavingCalculatorProps;
  PaymentRadio: PaymentRadioProps;
};

export const config = (): Config<Props> => {
  const componentList = createComponentConfigs({
    useAppDispatch,
    useAppSelector,
    type: 'checkout',
    baseService,
  });
  return {
    categories: {
      Blocks: {
        components: [
          'Columns',
          'Flex',
          'VerticalSpace',
          'Container',
          'Accordion',
          'Divider',
        ],
        defaultExpanded: false,
      },
      Text: {
        components: ['TextField', 'TextEditor'],
        defaultExpanded: false,
      },
      Buttons: {
        components: ['Button', 'ConfirmPurchase', 'ReturnButton'],
        defaultExpanded: false,
      },
      'Page Headers': {
        components: ['SimplePageHeader'],
        defaultExpanded: false,
      },
      'Form Components': {
        components: [
          'ContactInfo',
          'ShippingForm',
          'BillingForm',
          'ShippingMethodSelect',
          'Payment',
          'PaymentRadio',
        ],
        defaultExpanded: false,
      },
      'Checkout Components': {
        components: [
          'Cart',
          'OrderDetails',
          'PromoCode',
          'UpSell',
          'CustomerGuarantees',
          'CustomerReviews',
          'TermsAndSub',
          'Timer',
        ],
        defaultExpanded: false,
      },
      other: {
        defaultExpanded: false,
        components: ['SavingCalculator', 'ImageUploader', 'Stepper'],
      },
    },
    components: componentList as {
      [ComponentName in keyof Props]: Omit<
        ComponentConfig<Props[ComponentName], Props[ComponentName]>,
        'type'
      >;
    },
    root: {
      fields: {
        fontFamily: {
          type: 'custom',
          render: ({ value, onChange }) => {
            return (
              <FieldLabel label="Font Family" el="div">
                <AutoField
                  field={{
                    type: 'select',
                    options: fontFamilies,
                  }}
                  value={value}
                  onChange={e => {
                    onChange(e);
                  }}
                />
              </FieldLabel>
            );
          },
        },
        themeColor: {
          type: 'custom',
          label: 'Theme Color',
          render: ({ value, onChange }) => (
            <ThemeColorComponent value={value} onChange={onChange} />
          ),
        },
        errorColor: {
          type: 'custom',
          label: 'Error Color',
          render: ({ value, onChange }) => (
            <ErrorColor value={value} onChange={onChange} />
          ),
        },
        pixelTracking: {
          type: 'custom',
          label: 'Pixel Tracking',
          render: ({ value, onChange }) => {
            return <PixelTracking value={value} onChange={onChange} />;
          },
        },
        customCss: {
          type: 'custom',
          label: 'Custom CSS',
          render: ({ value, onChange }) => (
            <CustomCss value={value} onChange={onChange} />
          ),
        },
        customScripts: {
          type: 'custom',
          label: 'Custom Scripts',
          render: ({ value, onChange }) => (
            <CustomScripts value={value} onChange={onChange} />
          ),
        },
      },
      defaultProps: {
        fontFamily: fontFamilies[0].value,
        themeColor: { r: 79, g: 70, b: 229 },
        errorColor: '#FF5A50',
        customCss: [],
        customScripts: [],
      },
      render: ({
        children,
        fontFamily,
        errorColor,
        customCss,
        customScripts,
      }) => (
        <PuckRenderPage
          fontFamily={fontFamily}
          errorColor={errorColor}
          customCss={customCss}
          customScripts={customScripts}
          useAppSelector={useAppSelector}>
          {children}
        </PuckRenderPage>
      ),
    },
  };
};

export default config;
